/**
 * Shared saved-plan vs last build-handoff comparison. These markers are written
 * when a plan is handed to the build pipeline, before completeBuild may mark the
 * build terminal, so callers decide separately whether to use "sent" or "built"
 * copy.
 */
export interface PlanBuiltComparison {
  planRevision?: number;
  planHash?: number;
  lastBuiltPlanRevision?: number;
  lastBuiltPlanHash?: number;
}

/** A plan has been handed to the build pipeline at least once. */
export function hasBuiltRevision(c: PlanBuiltComparison): boolean {
  return c.lastBuiltPlanRevision !== undefined || c.lastBuiltPlanHash !== undefined;
}

/**
 * True when the saved plan differs from the last-built plan. Hash comparison
 * wins when both sides carry hashes (revision counters can drift across
 * restore/import); falls back to revision numbers.
 */
export function savedPlanDiffersFromBuilt(c: PlanBuiltComparison): boolean {
  const revisionDiffers =
    c.planRevision !== undefined &&
    c.lastBuiltPlanRevision !== undefined &&
    c.lastBuiltPlanRevision !== c.planRevision;
  const hashDiffers =
    c.planHash !== undefined &&
    c.lastBuiltPlanHash !== undefined &&
    c.lastBuiltPlanHash !== c.planHash;
  return c.planHash !== undefined && c.lastBuiltPlanHash !== undefined
    ? hashDiffers
    : c.planRevision !== undefined && revisionDiffers;
}

export function savedPlanMatchesBuilt(c: PlanBuiltComparison): boolean {
  return hasBuiltRevision(c) && !savedPlanDiffersFromBuilt(c);
}
